/**
 * Percorre todos os nós de texto dentro de um range e aplica uma função
 */
function processTextNodes(
  range: Range,
  processor: (textNode: Text, parentElement: HTMLElement) => void
): void {
  const startContainer = range.startContainer;
  const endContainer = range.endContainer;
  const commonAncestor = range.commonAncestorContainer;

  // Função para verificar se um nó está dentro do range
  const isNodeInRange = (node: Node): boolean => {
    const nodeRange = document.createRange();
    nodeRange.selectNode(node);
    
    const startCompare = nodeRange.compareBoundaryPoints(Range.START_TO_START, range);
    const endCompare = nodeRange.compareBoundaryPoints(Range.END_TO_END, range);
    
    return startCompare >= 0 && endCompare <= 0;
  };

  // Percorre recursivamente todos os nós
  const walker = document.createTreeWalker(
    commonAncestor,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: (node) => {
        if (isNodeInRange(node)) {
          return NodeFilter.FILTER_ACCEPT;
        }
        return NodeFilter.FILTER_SKIP;
      }
    }
  );

  const textNodes: Text[] = [];
  let currentNode = walker.nextNode();
  
  while (currentNode) {
    if (currentNode.nodeType === Node.TEXT_NODE && currentNode.textContent?.trim()) {
      textNodes.push(currentNode as Text);
    }
    currentNode = walker.nextNode();
  }

  // Aplica processador a cada nó de texto
  textNodes.forEach(textNode => {
    const parent = textNode.parentElement;
    if (parent) {
      processor(textNode, parent);
    }
  });
}

/**
 * Aplica formatação inline de forma robusta, SEM deletar conteúdo
 */
export function wrapSelectionInline(
  styleApplier: (el: HTMLElement) => void,
  root: HTMLElement
): boolean {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return false;

  const range = sel.getRangeAt(0);
  if (range.collapsed) return false;

  try {
    // Se a seleção está em um único elemento, usa método simples
    if (range.startContainer === range.endContainer) {
      const frag = range.extractContents();
      const span = document.createElement('span');
      styleApplier(span);
      span.appendChild(frag);
      range.insertNode(span);
      
      // Reposiciona cursor
      sel.removeAllRanges();
      const newRange = document.createRange();
      newRange.selectNodeContents(span);
      newRange.collapse(false);
      sel.addRange(newRange);
      
      return true;
    }

    // Para seleções complexas (múltiplos elementos), aplica estilo nó por nó
    processTextNodes(range, (textNode, parentElement) => {
      // Cria span com estilo
      const span = document.createElement('span');
      styleApplier(span);
      
      // Move o nó de texto para dentro do span
      const clonedText = textNode.cloneNode(true);
      span.appendChild(clonedText);
      
      // Substitui o nó original
      if (textNode.parentNode) {
        textNode.parentNode.replaceChild(span, textNode);
      }
    });

    // Normaliza para limpar estrutura
    root.normalize();

    // Reposiciona cursor no final
    sel.removeAllRanges();
    const newRange = document.createRange();
    newRange.selectNodeContents(root);
    newRange.collapse(false);
    sel.addRange(newRange);

    return true;
  } catch (error) {
    console.error('RobustFormatting: Error wrapping selection:', error);
    return false;
  }
}

/**
 * Aplica tamanho da fonte de forma robusta
 */
export function applyFontSize(size: string, root: HTMLElement): boolean {
  return wrapSelectionInline((el) => {
    el.style.fontSize = size;
    el.style.lineHeight = '1.4'; // Melhora leitura
    el.style.fontFamily = '"Times New Roman", serif';
  }, root);
}

/**
 * Aplica cor do texto de forma robusta
 */
export function applyTextColor(color: string, root: HTMLElement): boolean {
  return wrapSelectionInline((el) => {
    el.style.color = color;
    el.style.fontFamily = '"Times New Roman", serif';
  }, root);
}

/**
 * Aplica cor de fundo de forma robusta
 */
export function applyBackgroundColor(color: string, root: HTMLElement): boolean {
  return wrapSelectionInline((el) => {
    el.style.backgroundColor = color;
    el.style.fontFamily = '"Times New Roman", serif';
  }, root);
}

/**
 * Aplica família da fonte de forma robusta
 */
export function applyFontFamily(fontFamily: string, root: HTMLElement): boolean {
  return wrapSelectionInline((el) => {
    el.style.fontFamily = `"${fontFamily}", serif`;
  }, root);
}